import React, { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import Link from "../components/Link";
import WpImage from "../components/WpImage";

// Card outline: rounded rectangle with a notch cut out of the bottom-right
// corner, where the arrow sits. Drawn per card size, since the notch must keep
// its size while the card stretches.
const RADIUS = 12;
const NOTCH = 64;
const NOTCH_RADIUS = 16;

const outline = (w, h) =>
    [
        `M ${RADIUS} 0`,
        `L ${w - RADIUS} 0`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${w} ${RADIUS}`,
        `L ${w} ${h - NOTCH - RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${w - RADIUS} ${h - NOTCH}`,
        `L ${w - NOTCH + NOTCH_RADIUS} ${h - NOTCH}`,
        `A ${NOTCH_RADIUS} ${NOTCH_RADIUS} 0 0 0 ${w - NOTCH} ${
            h - NOTCH + NOTCH_RADIUS
        }`,
        `L ${w - NOTCH} ${h - RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${w - NOTCH - RADIUS} ${h}`,
        `L ${RADIUS} ${h}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 0 ${h - RADIUS}`,
        `L 0 ${RADIUS}`,
        `A ${RADIUS} ${RADIUS} 0 0 1 ${RADIUS} 0 Z`,
    ].join(" ");

const useElementSize = () => {
    const ref = useRef(null);
    const [size, setSize] = useState(null);

    useEffect(() => {
        const element = ref.current;
        if (!element || typeof ResizeObserver === "undefined") return;

        const update = () => {
            const width = Math.round(element.clientWidth);
            const height = Math.round(element.clientHeight);
            if (!width || !height) return;

            setSize((current) =>
                current?.width === width && current?.height === height
                    ? current
                    : { width, height }
            );
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return [ref, size];
};

const ArrowIcon = () => (
    <svg
        viewBox="0 0 22 22"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M6.5 15.5 15.5 6.5" />
        <path d="M8.6 6.5h6.9v6.9" />
    </svg>
);

const SolutionCard = ({ icon, title, description, link }) => {
    const [ref, size] = useElementSize();
    const path = size ? outline(size.width, size.height) : null;
    const clipPath = path ? `path("${path}")` : undefined;

    return (
        <Link
            link={link}
            className="solution-cards__card"
            title={link?.title || title}
        >
            {/* Link does not forward refs, so measure an inset frame */}
            <span className="solution-cards__frame" ref={ref} />
            <span
                className="solution-cards__fill"
                style={{ clipPath, WebkitClipPath: clipPath }}
            />
            {path && (
                <svg
                    className="solution-cards__edge"
                    viewBox={`-1 -1 ${size.width + 2} ${size.height + 2}`}
                    aria-hidden="true"
                >
                    <path d={path} />
                </svg>
            )}

            <span className="solution-cards__body">
                {icon && (
                    <span className="solution-cards__icon">
                        <WpImage image={icon} alt="" />
                    </span>
                )}
                {title && (
                    <span className="solution-cards__title">{title}</span>
                )}
                {description && (
                    <span className="solution-cards__text">{description}</span>
                )}
            </span>

            <span className="solution-cards__arrow">
                <ArrowIcon />
            </span>
        </Link>
    );
};

const SolutionCards = ({ category, header, items, className }) => {
    const cards = items?.filter(({ title, link }) => title && link?.url) || [];

    if (cards.length === 0) return null;

    return (
        <section className={`solution-cards ${className || ""}`}>
            <Container>
                {(category || header) && (
                    <div className="solution-cards__intro">
                        {category && (
                            <p
                                className="solution-cards__label"
                                dangerouslySetInnerHTML={{ __html: category }}
                            />
                        )}
                        {header && (
                            <h2
                                className="solution-cards__header"
                                dangerouslySetInnerHTML={{ __html: header }}
                            />
                        )}
                    </div>
                )}

                <div className="solution-cards__grid">
                    {cards.map((card, index) => (
                        <SolutionCard key={index} {...card} />
                    ))}
                </div>
            </Container>
        </section>
    );
};

export default SolutionCards;
